import { useEffect, useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { getInvoices } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import StatusBadge, { OverdueDays } from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import EmptyState from '../components/ui/EmptyState'
import { useUserMode } from '../contexts/UserModeContext'
import { useAuth } from '../contexts/AuthContext'

const STATUSES = ['ALL', 'DRAFT', 'APPROVED', 'SENT', 'PARTIAL', 'PAID', 'OVERDUE', 'CANCELLED']

export default function Invoices() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [allInvoices, setAllInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const status = searchParams.get('status') || 'ALL'
  const { isSimple } = useUserMode()
  const { isInvoiceCreator } = useAuth()
  // The restricted invoice-creator role must see no monetary figures in the list.
  const hideAmounts = isInvoiceCreator

  // Fetch ALL invoices once for client-side sorting/filtering
  useEffect(() => {
    setLoading(true)
    getInvoices({ take: 9999 })
      .then((res) => setAllInvoices(res.data.invoices || []))
      .catch(() => setAllInvoices([]))
      .finally(() => setLoading(false))
  }, [])

  // Count invoices per status (computed from full dataset, before search filter)
  const statusCounts = useMemo(() => {
    const counts = { ALL: allInvoices.length }
    STATUSES.forEach((s) => { if (s !== 'ALL') counts[s] = 0 })
    allInvoices.forEach((inv) => {
      if (counts[inv.status] !== undefined) counts[inv.status]++
    })
    return counts
  }, [allInvoices])

  // Client-side filtering by status and search
  const filtered = useMemo(() => {
    let list = allInvoices
    if (status !== 'ALL') {
      list = list.filter((inv) => inv.status === status)
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter((inv) =>
        (inv.number || '').toLowerCase().includes(q) ||
        (inv.customer?.name || '').toLowerCase().includes(q)
      )
    }
    return list
  }, [allInvoices, status, search])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(filtered, {
    defaultSort: 'date',
    defaultDir: 'desc',
    pageSize: 25,
    storageKey: 'invoices',
  })

  const setStatus = (s) => {
    const p = new URLSearchParams(searchParams)
    if (s === 'ALL') p.delete('status')
    else p.set('status', s)
    setSearchParams(p)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
        <Link to="/invoices/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Invoice
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search invoices..."
            className="input pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                status === s
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {s}
              <span className={`ml-1 ${status === s ? 'text-brand-500' : 'text-gray-400'}`}>
                {statusCounts[s] ?? 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="space-y-3 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            type="invoices"
            message={status !== 'ALL' || search ? 'No invoices match your filters.' : 'No invoices yet. Create your first one.'}
            actionLabel={status === 'ALL' && !search ? 'New Invoice' : undefined}
            actionTo={status === 'ALL' && !search ? '/invoices/new' : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b">
                    <SortHeader label="Invoice #" sortKey="number" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Customer" sortKey="customer.name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    {!hideAmounts && (
                      <SortHeader label="Amount" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    )}
                    {!isSimple && !hideAmounts && (
                      <SortHeader label="Balance" sortKey="balanceDue" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    )}
                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((inv) => {
                    const isOverdue = inv.status === 'OVERDUE'
                    return (
                      <tr key={inv.id} className="border-b last:border-0 hover:bg-gray-50/70 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/invoices/${inv.id}`} className="text-brand-600 hover:underline font-medium">
                            {inv.number || 'DRAFT'}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Avatar name={inv.customer?.name} />
                            <span className="text-gray-600">{inv.customer?.name || '\u2014'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(inv.date)}</td>
                        {!hideAmounts && (
                          <td className="px-4 py-3 text-right">{formatCurrency(inv.total, inv.currency)}</td>
                        )}
                        {!isSimple && !hideAmounts && (
                          <td className={`px-4 py-3 text-right ${isOverdue && inv.balanceDue > 0 ? 'text-red-600 font-medium' : ''}`}>
                            {formatCurrency(inv.balanceDue, inv.currency)}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <StatusBadge status={inv.status} />
                            {isOverdue && <OverdueDays dueDate={inv.dueDate} />}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  )
}
